<script lang="ts">
	import { goto } from '$app/navigation';
	import { resolve } from '$app/paths';
	import { page } from '$app/state';
	import CardsFloating from '$lib/pages/landingpage/ui/CardsFloating.svelte';
	import MarketingButton from '$lib/shared/ui/MarketingButton.svelte';
	import Copyright from '$lib/shared/ui/Copyright.svelte';
	import AvatarComponent from '$lib/shared/ui/AvatarComponent.svelte';
	import CrownLeaderboardLink from '$lib/shared/ui/CrownLeaderboardLink.svelte';
	import ThemeToggle from '$components/ThemeToggle.svelte';
	import PopupAccountBox from '$lib/features/user/ui/PopupAccountBox.svelte';

	let { user, children } = $props();
	const smallLogoLightSrc = '/static/images/logo/supertip-s-black.avif';
	const smallLogoDarkSrc = '/static/images/logo/supertip-s-white.avif';
	const logoLightSrc = '/static/images/logo/supertip-revised-black.avif';
	const logoDarkSrc = '/static/images/logo/supertip-revised-white.avif';
	const lightLeft = '/static/images/light-left.avif';
	const lightRight = '/static/images/light-right.avif';
	let btnClass = 'rounded-full px-2.5 py-1';

	function isHashSelected(hash: string, currentHash: string): string {
		return isSelectedClasses(currentHash === hash);
	}
	function isPathSelected(pathname: string, currentPathname: string): string {
		const selected = currentPathname === `/${pathname}` || currentPathname === `/${pathname}/`;
		return isSelectedClasses(selected);
	}
	function isSelectedClasses(selected: boolean) {
		return [
			btnClass,
			selected
				? ' preset-filled-surface-500 border border-secondary-100-900 bg-surface-500/30 text-surface-950-50'
				: ''
		].join(' ');
	}

	function shouldHandleNavigationClick(event: MouseEvent): boolean {
		return !(
			event.defaultPrevented ||
			event.button !== 0 ||
			event.metaKey ||
			event.ctrlKey ||
			event.shiftKey ||
			event.altKey
		);
	}

	function scrollToTop() {
		const prefersReducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
		window.scrollTo({ top: 0, behavior: prefersReducedMotion ? 'auto' : 'smooth' });
	}

	async function scrollHome(event: MouseEvent) {
		if (!shouldHandleNavigationClick(event)) {
			return;
		}

		event.preventDefault();
		await goto(resolve('/'), { noScroll: true, keepFocus: true });
		scrollToTop();
	}

	async function scrollToSection(event: MouseEvent, id: string) {
		if (!shouldHandleNavigationClick(event)) {
			return;
		}

		event.preventDefault();
		await goto(resolve(`/#${id}`), { noScroll: true, keepFocus: true });

		const target = document.getElementById(id);
		if (!target) {
			return;
		}

		const headerOffset = document.getElementById('header')?.offsetHeight ?? 0;
		const topPadding = 24;
		const top = target.getBoundingClientRect().top + window.scrollY - headerOffset - topPadding;
		const prefersReducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;

		window.scrollTo({ top: Math.max(top, 0), behavior: prefersReducedMotion ? 'auto' : 'smooth' });
	}

	let featuresClass = $derived(isHashSelected('#features', page.url.hash));
	let howitworksClass = $derived(isHashSelected('#howitworks', page.url.hash));
	let faqClass = $derived(isHashSelected('#faq', page.url.hash));
	let leaderboardClass = $derived(isPathSelected('leaderboard', page.url.pathname));

	let iconClasses = 'lg:max-h-10';
	let iconStyles = 'max-width: 2rem';
</script>

<div class="grid grid-rows-[auto_1fr_auto]">
	<!-- Decorations -->
	{#if page.url.pathname === '/'}
		<div>
			<div
				class="pointer-events-none absolute -top-[10rem] h-full w-screen overflow-hidden bg-transparent"
			>
				<CardsFloating />
			</div>
		</div>
		<div class="pointer-events-none absolute top-32 left-16 h-[40vw] w-[40vw] sm:top-16">
			<img
				src="/static/images/bg-oval-left.avif"
				alt=""
				draggable="false"
				class="pointer-events-none z-0 transform-none"
			/>
		</div>
		<div class="pointer-events-none absolute top-32 right-16 h-[40vw] w-[40vw] sm:top-16">
			<img
				src="/static/images/bg-oval-right.avif"
				alt=""
				draggable="false"
				class="pointer-events-none z-0 transform-none"
			/>
		</div>
	{/if}
	<div class="pointer-events-none absolute top-0 left-0 z-0 h-1/5 w-1/5">
		<img src={lightLeft} alt="" class="" draggable="false" />
	</div>
	<div class="pointer-events-none absolute top-0 right-0 z-0 h-1/5 w-1/5">
		<img src={lightRight} alt="" class="" draggable="false" />
	</div>

	<!-- Header -->
	<header id="header" class="sticky top-0 z-10">
		<div class="flex items-center justify-center pt-2 md:pt-4">
			<div
				class="flex h-9 w-[95%] flex-row items-center justify-between rounded-full border border-surface-100 bg-surface-50-950 py-1 lg:h-12 lg:w-4/5 lg:py-6 2xl:w-1/2 dark:border-surface-800!"
			>
				<!-- Logo -->
				<div class="flex items-center gap-2">
					<div class="pl-0 lg:hidden dark:hidden {btnClass}">
						<a href={resolve('/')} onclick={scrollHome}
							><img
								src={smallLogoLightSrc}
								class={iconClasses}
								style={iconStyles}
								alt="small light logo"
							/></a
						>
					</div>
					<div class="hidden lg:block lg:dark:hidden {btnClass}">
						<a href={resolve('/')} onclick={scrollHome}
							><img src={logoLightSrc} class={iconClasses} alt="light logo" /></a
						>
					</div>

					<div class="hidden pl-0 dark:block lg:dark:hidden {btnClass}">
						<a href={resolve('/')} onclick={scrollHome}
							><img
								src={smallLogoDarkSrc}
								class={iconClasses}
								style={iconStyles}
								alt="small dark logo"
							/></a
						>
					</div>
					<div class="hidden lg:dark:block {btnClass}">
						<a href={resolve('/')} onclick={scrollHome}
							><img src={logoDarkSrc} class={iconClasses} alt="dark logo" /></a
						>
					</div>
				</div>

				<!-- Links -->
				<div
					class="flex items-center text-sm text-secondary-200 lg:text-base dark:text-secondary-700"
				>
					<div class={featuresClass}>
						<a href={resolve('/#features')} onclick={(event) => scrollToSection(event, 'features')}
							>Features</a
						>
					</div>
					<div class="{howitworksClass} hidden sm:block">
						<a
							href={resolve('/#howitworks')}
							onclick={(event) => scrollToSection(event, 'howitworks')}>How it Works</a
						>
					</div>
					<div class="{faqClass} hidden sm:block">
						<a href={resolve('/#faq')} onclick={(event) => scrollToSection(event, 'faq')}>FAQ</a>
					</div>
					<div class={leaderboardClass}><a href={resolve('/leaderboard')}>Leaderboard</a></div>
					<div class={btnClass}>
						<a href="https://help.supertip.gg" target="_blank" rel="noopener noreferrer">Help</a>
					</div>
				</div>

				<!-- Theme & login/profile -->
				<div class="flex items-center space-x-1 pr-2 lg:space-x-4">
					<ThemeToggle />
					<span class="desktop-only vr h-6 border-surface-800 opacity-40 dark:border-surface-100"
					></span>
					<div class="py-1">
						<AvatarComponent logged={user} src={user?.avatar}>
							<PopupAccountBox {user} />
						</AvatarComponent>
					</div>
				</div>
			</div>
		</div>
	</header>
	<!-- Main Content -->
	<main class="w-screen space-y-4">
		{@render children?.()}
	</main>
	<!-- Footer -->
	<footer class="container mx-auto max-w-screen-2xl p-6 md:w-3/4">
		<div class="grid grid-cols-1 gap-20 pb-10 lg:grid-cols-2">
			<div
				class="flex flex-col items-center justify-center space-y-5 rounded-lg border border-primary-400 p-10"
			>
				<p class="text-lg font-bold">
					Join thousands of streamers building stronger communities every day!
				</p>
				<div class="w-full">
					<MarketingButton href="/signin" variant="primary" showArrow={false} classes="w-full">
						Get started for free
					</MarketingButton>
				</div>
			</div>
			<div class="grid grid-cols-3">
				<div class="flex flex-col space-y-2">
					<div class="pb-5 text-xl">Product</div>
					<div>
						<a href={resolve('/#features')} onclick={(event) => scrollToSection(event, 'features')}
							>Features</a
						>
					</div>
					<div>
						<a
							href={resolve('/#howitworks')}
							onclick={(event) => scrollToSection(event, 'howitworks')}>How it Works</a
						>
					</div>
				</div>
				<div class="flex flex-col space-y-2">
					<div class="pb-5 text-xl">Company</div>
					<div>
						<a href={resolve('/#faq')} onclick={(event) => scrollToSection(event, 'faq')}>FAQ</a>
					</div>
					<div>
						<a href="https://help.supertip.gg" target="_blank" rel="noopener noreferrer"
							>Knowledge Base</a
						>
					</div>
					<div><a href="mailto:info@supertip.gg">Contact</a></div>
				</div>
				<div class="flex flex-col space-y-2">
					<div class="pb-5 text-xl">Legal</div>
					<div>
						<a href={resolve('/submission')}>Submission Terms</a>
					</div>
					<div>
						<a href={resolve('/terms')}>Terms of Service</a>
					</div>
					<div>
						<a href={resolve('/privacy')}>Privacy Policy</a>
					</div>
				</div>
			</div>
		</div>
		<div class="flex flex-col items-center justify-center py-5">
			<CrownLeaderboardLink />
			<Copyright />
		</div>
	</footer>
</div>
